/**
 * UI-6b Task 12 — ActiveCallsCounter widget renderer.
 *
 * Phase 1 status: PLACEHOLDER. The intended data source is a real-time
 * Supervisor endpoint (e.g. `/supervisor/realtime`) that reports the
 * currently active call count. No such HTTP endpoint exists in the API
 * yet — the live counter requires Supervisor view, which lands in a
 * later milestone.
 *
 * To keep the widget honest:
 *   - the big number reads "—" rather than 0 (which would be a lie)
 *   - the setInterval skeleton is still wired up at the documented 10s
 *     cadence so the Phase 2 swap is a one-line change inside the tick
 *     callback (replace the no-op with an `api.get<{count:number}>(...)`).
 *   - the interval is cleaned up on unmount via the effect's return.
 */
import { useEffect, useState, type JSX } from 'react'

export type ActiveCallsCounterConfig = Record<string, never>

export const REFRESH_INTERVAL_MS = 10_000

export function ActiveCallsCounterRenderer(
  _props: { config: ActiveCallsCounterConfig },
): JSX.Element {
  // Tracked even though we render "—" today, so Phase 2 only needs to
  // replace the no-op tick body with an `api.get(...)` call.
  const [count, _setCount] = useState<number | null>(null)

  useEffect(() => {
    const tick = (): void => {
      // Phase 1: no-op. Phase 2 will:
      //   api.get<{count:number}>('/supervisor/realtime').then(r => setCount(r.count))
    }
    tick()
    const handle = setInterval(tick, REFRESH_INTERVAL_MS)
    return (): void => {
      clearInterval(handle)
    }
  }, [])

  const display = count == null ? '—' : String(count)

  return (
    <div
      data-testid="active-calls-counter-widget"
      className="flex h-full w-full flex-col items-center justify-center gap-1 p-2 text-center"
    >
      <div className="text-4xl font-bold tabular-nums text-gray-800">{display}</div>
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
        Active calls
      </div>
      <div className="mt-1 max-w-[14rem] text-[10px] leading-snug text-gray-400">
        Live counter requires Supervisor view.
      </div>
    </div>
  )
}
